import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageComponent, Button, Skeletons } from '../../components';
import Api from '../../Api';
import { FiCheckCircle, FiBriefcase } from 'react-icons/fi';
import './style.css';

export default function RiskEmployeeFormPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [formInfo, setFormInfo] = useState(null);
    const [responses, setResponses] = useState({});
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        loadForm();
    }, [token]);

    const loadForm = async () => {
        try {
            const response = await Api.get(`/risk/public/${token}`);
            setFormInfo(response.data);
            
            // Initialize responses
            const initialResponses = {};
            response.data.formStructure.questions.forEach(q => {
                initialResponses[q.id] = '';
            });
            setResponses(initialResponses);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Erro ao carregar formulário.';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const companyLogoUrl = useMemo(() => {
        if (!formInfo || !formInfo.companyLogo) return null;
        
        const logo = formInfo.companyLogo;
        if (logo.type === 'Buffer' && logo.data) {
            const base64String = btoa(
                new Uint8Array(logo.data).reduce(
                    (data, byte) => data + String.fromCharCode(byte),
                    ''
                )
            );
            return `data:image/png;base64,${base64String}`;
        }
        
        if (typeof logo === 'string') return logo;
        
        return null;
    }, [formInfo]);

    const handleOptionChange = (questionId, option) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: option
        }));
    };

    const handleSubmit = async () => {
        // Validate required questions
        const missingRequired = formInfo.formStructure.questions.filter(q => 
            q.required && !responses[q.id]
        );

        if (missingRequired.length > 0) {
            toast.warning('Por favor, responda todas as perguntas obrigatórias.');
            return;
        }

        try {
            await Api.post(`/risk/public/${token}`, { responses });
            setSubmitted(true);
            toast.success('Respostas enviadas com sucesso!');
        } catch (error) {
            toast.error('Erro ao enviar respostas.');
        }
    };

    if (loading) {
        return (
            <PageComponent authenticatedPage={false}>
                <div className="container">
                    <Skeletons.TableSkeleton />
                </div>
            </PageComponent>
        );
    }

    if (!formInfo && !loading) {
        return (
            <PageComponent authenticatedPage={false}>
                <div className="container">
                    <div className="public-form-wrapper" style={{ textAlign: 'center' }}>
                        <h1>Formulário não encontrado</h1>
                        <p>O link pode estar expirado ou já ter sido respondido.</p>
                    </div>
                </div>
            </PageComponent>
        );
    }

    if (submitted) {
        return (
            <PageComponent authenticatedPage={false}>
                <div className="container">
                    <div className="public-form-wrapper">
                        <div className="success-container">
                            <FiCheckCircle size={80} className="success-icon" />
                            <h2>Obrigado!</h2>
                            <p>Suas respostas foram enviadas com sucesso.</p>
                            <p>Você já pode fechar esta página.</p>
                        </div>
                    </div>
                </div>
            </PageComponent>
        );
    }

    return (
        <PageComponent authenticatedPage={false}>
            <div className="container">
                <div className="public-form-wrapper">
                    <div className="public-form-company-header">
                        <div className="company-logo-container">
                            {companyLogoUrl ? (
                                <img src={companyLogoUrl} alt={formInfo.companyName} />
                            ) : (
                                <FiBriefcase size={32} />
                            )}
                        </div>
                        <div className="company-info-text">
                            <h2>{formInfo.companyName}</h2>
                        </div>
                    </div>
                    
                    <div className="public-form-header">
                        <h1>{formInfo.mappingName}</h1>
                        {formInfo.description && <p>{formInfo.description}</p>}
                        <div className="employee-info">
                            Colaborador: {formInfo.employeeName}
                        </div>
                    </div>

                    <div className="public-questions-list">
                        {formInfo.formStructure.questions.map((q, index) => (
                            <div key={q.id} className="public-question-card">
                                <div className="public-question-title">
                                    {index + 1}. {q.title}
                                    {q.required && <span className="required-mark">*</span>}
                                </div>
                                <div className="public-options-list">
                                    {q.options.map((opt, optIndex) => (
                                        <label 
                                            key={optIndex} 
                                            className={`public-option-label ${responses[q.id] === opt ? 'selected' : ''}`}
                                        >
                                            <input 
                                                type="radio" 
                                                name={`question-${q.id}`} 
                                                value={opt}
                                                checked={responses[q.id] === opt}
                                                onChange={() => handleOptionChange(q.id, opt)}
                                            />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="public-form-footer">
                        <Button className="btn-success submit-btn" onClick={handleSubmit}>
                            Enviar Respostas
                        </Button>
                    </div>
                </div>
            </div>
        </PageComponent>
    );
}
